// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { Typography } from "@insite/mobius/index";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@stanion/App/Components/Common/XngZone";
import { color, xScreen } from "@stanion/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: InjectableCss;
    containerSecond?: InjectableCss;
    productTitleMobile?: HiddenProps;
    mobilePartNumbers?: HiddenProps;
    badgeSectionMobile?: HiddenProps;
    leftSection?: InjectableCss;
    mainContent?: InjectableCss;
    imagesSection?: InjectableCss;
    detailsSection?: InjectableCss;
    rightSection?: InjectableCss;
    actionsWrapper?: InjectableCss;
    bottomSection?: InjectableCss;
    badgeSection?: InjectableCss;
    imagebadgeSection?: InjectableCss;
    imageBadgeSectionMobile?: InjectableCss;
    siteMessageContainer?: InjectableCss;
    siteMessageText?: TypographyProps;
    carouselSection?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    container: {
        css: css`
            display: flex;
            margin-bottom: 62px;
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    containerSecond: {
        css: css`
            display: flex;
            width: 100%;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    leftSection: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 0 1 100%;
            margin-right: 22px;
            ${xScreen("lte", "xs")} {
                margin-right: 0;
                > div {
                    display: block;
                }
            }
        `,
    },
    mainContent: {
        css: css`
            display: flex;
        `,
    },
    imagesSection: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 0 0 317px;
            margin-right: 22px;
            img {
                width: auto;
                height: auto;
                max-width: 100%;
            }
            ${xScreen("lte", "md")} {
                flex: 0 1 100%;
            }
            ${xScreen("lte", "xs")} {
                margin-right: 0;
            }
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 0 0 317px;
            ${xScreen("lte", "md")} {
                flex: 0 1 100%;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-radius: 2px;
            background: ${color("common.white")};
            padding: 16px;
            @media print {
                break-inside: avoid;
            }
        `,
    },
    bottomSection: {
        css: css`
            width: 100%;
        `,
    },
    productTitleMobile: {
        css: css`
            flex-basis: 100%;
        `,
    },
    mobilePartNumbers: {
        css: css`
            margin-bottom: 28px;
            ${xScreen("lte", "xs")} {
                margin-top: 24px;
            }
        `,
    },
    badgeSection: {
        css: css`
            > div {
                margin-bottom: 11px;
                display: flex;
                align-items: baseline;
                column-gap: 10px;
            }
        `,
    },
    badgeSectionMobile: {
        css: css`
            > div {
                margin-bottom: 22px;
                display: flex;
                align-items: baseline;
                column-gap: 10px;
            }
        `,
    },
    siteMessageContainer: {
        css: css`
            margin-top: 15px;
        `,
    },
    carouselSection: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 64px;
            @media print {
                > div {
                    display: none;
                }
            }
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps> = props => {
    // XNG-Changes structure
    return (
        <>
            <StyledWrapper {...styles.container} data-test-selector="productDetailsView">
                <Hidden above="md" {...styles.productTitleMobile}>
                    <XngZone contentId={props.id} zoneName="Content5" hint="Product Title" />
                </Hidden>
                <Hidden above="md" {...styles.badgeSectionMobile}>
                    <XngZone contentId={props.id} zoneName="Content6" hint="Zone For Badges" />
                </Hidden>
                <Hidden above="md" {...styles.imageBadgeSectionMobile}>
                    <XngZone contentId={props.id} zoneName="imagebadgeSection" hint="Zone For Image Badges" />
                </Hidden>
                <StyledWrapper {...styles.containerSecond}>
                    <StyledWrapper {...styles.leftSection} data-test-selector="productDetails_leftColumn">
                        <StyledWrapper {...styles.mainContent}>
                            <StyledWrapper id="Content0" {...styles.imagesSection}>
                                <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Image" />
                                <StyledWrapper {...styles.siteMessageContainer}>
                                    <Typography {...styles.siteMessageText}>
                                        {siteMessage("Custom_PDP_ProductOrWebsiteData_Issues")}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                            <Hidden below="lg">
                                <StyledWrapper id="Content1" {...styles.detailsSection}>
                                    <XngZone contentId={props.id} zoneName="Content5" hint="Zone For Product Title" />
                                    <StyledWrapper id="Content1" {...styles.badgeSection}>
                                        <XngZone contentId={props.id} zoneName="Content6" hint="Zone For Badges" />
                                    </StyledWrapper>
                                    <StyledWrapper id="Content7" {...styles.imagebadgeSection}>
                                        <XngZone
                                            contentId={props.id}
                                            zoneName="imagebadgeSection"
                                            hint="Zone For Image Badges"
                                        />
                                    </StyledWrapper>
                                    <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Details" />
                                </StyledWrapper>
                            </Hidden>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSection} data-test-selector="productDetails_rightColumn">
                        <Hidden above="md" {...styles.mobilePartNumbers}>
                            <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Details" />
                        </Hidden>
                        <StyledWrapper {...styles.actionsWrapper}>
                            <XngZone contentId={props.id} zoneName="Content3" hint="Zone For Summary & Actions" />
                        </StyledWrapper>
                        <XngZone contentId={props.id} zoneName="Content4" hint="bottom Actions" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper data-test-selector="productDetails_bottomSection" {...styles.bottomSection}>
                <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Specifications" />
            </StyledWrapper>
            <StyledWrapper data-test-selector="productDetails_bottomSection" {...styles.carouselSection}>
                <XngZone contentId={props.id} zoneName="Content8" hint="Zone For Product Carousels" />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
